"use client"

import { useEffect, useState } from "react"
import Script from "next/script"

const INTERACTION_EVENTS = ["scroll", "pointerdown", "keydown", "touchstart"] as const

/**
 * Third-party tags load after first user interaction, or after idle/timeout,
 * so they do not compete with LCP. Google Maps is loaded only on Places forms.
 */
const ScriptConfig = () => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (ready) return

    let cancelled = false
    let idleId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const enable = () => {
      if (cancelled) return
      setReady(true)
      cleanup()
    }

    const cleanup = () => {
      INTERACTION_EVENTS.forEach(event => {
        window.removeEventListener(event, enable)
      })
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }

    INTERACTION_EVENTS.forEach(event => {
      window.addEventListener(event, enable, { once: true, passive: true })
    })

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(enable, { timeout: 6000 })
    } else {
      timeoutId = setTimeout(enable, 6000)
    }

    return () => {
      cancelled = true
      cleanup()
    }
  }, [ready])

  return (
    <>
      <meta name="p:domain_verify" content="07e1c181f3cc4e616ffd3660d8b77113" />
      {process.env.NODE_ENV === "production" && (
        <meta
          httpEquiv="Content-Security-Policy"
          content="upgrade-insecure-requests"
        />
      )}
      <meta
        name="facebook-domain-verification"
        content="segmirrlcq9va6miqk7twp527njerm"
      />
      <meta name="p:domain_verify" content="f06bebfff29fa97725d3501a2b8032db" />

      {ready && (
        <>
          {/* GA4 stays afterInteractive so first pageview is captured.
             Everything else moves to lazyOnload — they don't need to fire
             before the page is interactive. */}
          <Script
            src="https://www.googletagmanager.com/gtag/js?id=G-K96QPPKR8F"
            strategy="afterInteractive"
          />
          <Script id="ga-config" strategy="afterInteractive">
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-K96QPPKR8F');
          `}
          </Script>

          <Script id="facebook-pixel" strategy="lazyOnload">
            {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod ?
              n.callMethod.apply(n, arguments) : n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '6760111864033020');
            fbq('track', 'PageView');
          `}
          </Script>

          <Script
            src="//s1.what-on.com/widget/service.js?key=70Gl35&v=7"
            strategy="lazyOnload"
          />

          <Script id="ms-clarity" strategy="lazyOnload">
            {`
            (function(c,l,a,r,i,t,y){
              c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "ncau6if7c1");
          `}
          </Script>

          <Script id="gtm" strategy="lazyOnload">
            {`
            (function(w,d,s,l,i){w[l] = w[l] || [];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-WNJPJKZK');
          `}
          </Script>
        </>
      )}

      <noscript
        dangerouslySetInnerHTML={{
          __html:
            '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=6760111864033020&ev=PageView&noscript=1" alt="" />'
        }}
      />
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-WNJPJKZK"
          height="0"
          width="0"
          style={{
            display: "none",
            visibility: "hidden"
          }}
        />
      </noscript>

      <style>
        {`
          #super-cache-1s button {
            background-color: #cc9966 !important;
          }
          #super-cache-1s img {
            display: none !important;
          }
        `}
      </style>
    </>
  )
}

export default ScriptConfig
